'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { format, formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { useParams } from 'next/navigation';
import { getMedioById, getMediosSorted, type MedioData } from '@/data/medios';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'https://api.neotral.org';

interface Article {
  id: string;
  url: string;
  title: string;
  description?: string;
  published_at: string;
  fetched_at: string;
  political_leaning: number | null;
  truth_score: number | null;
  image_url: string | null;
  author?: string;
}

function BiasIndicator({ medio }: { medio: MedioData }) {
  const allMedios = getMediosSorted();
  const position = ((medio.biasScore + 100) / 200) * 100;
  
  return (
    <div className="bg-white border border-stone-200 rounded-lg p-4">
      <h4 className="text-xs font-mono uppercase tracking-wider text-stone-400 mb-3">
        Posición en el espectro
      </h4>
      <div className="relative">
        <div className="h-3 rounded-full bg-gradient-to-r from-red-400 via-stone-300 to-blue-400" />
        <div 
          className="absolute top-0 w-4 h-4 -mt-0.5 rounded-full bg-white border-2 border-stone-800 shadow transform -translate-x-1/2"
          style={{ left: `${position}%` }}
        />
      </div>
      <div className="flex justify-between text-xs text-stone-400 mt-2">
        <span>Izquierda</span>
        <span className="font-semibold text-stone-600">
          {medio.biasScore > 0 ? '+' : ''}{medio.biasScore}
        </span>
        <span>Derecha</span>
      </div>
    </div>
  );
}

function TransparencyBadge({ level }: { level: 'alta' | 'media' | 'baja' | 'opaca' }) {
  const config = {
    alta: { emoji: '🟢', text: 'Alta', color: 'text-green-600 bg-green-50 border-green-200' },
    media: { emoji: '🟡', text: 'Media', color: 'text-amber-600 bg-amber-50 border-amber-200' },
    baja: { emoji: '🟠', text: 'Baja', color: 'text-orange-600 bg-orange-50 border-orange-200' },
    opaca: { emoji: '🔴', text: 'Opaca', color: 'text-red-600 bg-red-50 border-red-200' },
  };
  
  const { emoji, text, color } = config[level];
  
  return (
    <span className={`inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1 rounded-full border ${color}`}>
      {emoji} {text}
    </span>
  );
}

function InfoSection({ 
  title, 
  icon, 
  children 
}: { 
  title: string; 
  icon: string; 
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white border border-stone-200 rounded-lg p-5">
      <h3 className="flex items-center gap-2 text-sm font-mono uppercase tracking-wider text-stone-400 mb-4">
        <span>{icon}</span>
        {title}
      </h3>
      {children}
    </div>
  );
}

function ArticleCard({ article, color, index }: { article: Article; color: 'red' | 'blue' | 'gray'; index: number }) {
  const borderColor = color === 'red' ? 'hover:border-l-red-400' : 
                      color === 'blue' ? 'hover:border-l-blue-400' : 
                      'hover:border-l-stone-400';

  const timeAgo = article.published_at 
    ? formatDistanceToNow(new Date(article.published_at), { locale: es, addSuffix: true })
    : null;

  return (
    <article 
      className={`group bg-white border-l-2 border-l-transparent ${borderColor} border border-stone-200 rounded-lg p-5 hover:shadow-md transition-all duration-200 opacity-0 animate-fade-in`}
      style={{ animationDelay: `${200 + index * 60}ms`, animationFillMode: 'forwards' }}
    >
      <a href={article.url} target="_blank" rel="noopener noreferrer" className="block">
        <div className="flex gap-4">
          <div className="flex-1 min-w-0">
            <h3 className="font-serif font-semibold text-stone-900 group-hover:text-stone-600 transition-colors line-clamp-2 mb-2">
              {article.title}
            </h3>
            
            {article.description && (
              <p className="text-sm text-stone-500 line-clamp-2 mb-3" style={{ fontFamily: 'Georgia, serif' }}>
                {article.description}
              </p>
            )}
            
            <div className="flex flex-wrap items-center gap-3 text-xs text-stone-400">
              {timeAgo && (
                <span className="flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {timeAgo}
                </span>
              )}
              {article.author && (
                <span className="flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  {article.author}
                </span>
              )}
              {article.political_leaning !== null && (
                <span className={`font-mono px-1.5 py-0.5 rounded ${
                  article.political_leaning < -10 ? 'bg-red-50 text-red-600' :
                  article.political_leaning > 10 ? 'bg-blue-50 text-blue-600' :
                  'bg-stone-100 text-stone-500'
                }`}>
                  {article.political_leaning > 0 ? '+' : ''}{Math.round(article.political_leaning)}
                </span>
              )}
            </div>
          </div>
          
          {article.image_url && (
            <div className="hidden sm:block w-24 h-20 flex-shrink-0 overflow-hidden rounded-lg bg-stone-100">
              <img 
                src={article.image_url} 
                alt=""
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                loading="lazy"
              />
            </div>
          )}
        </div>
      </a>
    </article>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="bg-white border border-stone-200 rounded-lg p-5 animate-pulse">
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="h-5 bg-stone-200 rounded w-3/4 mb-2" />
              <div className="h-4 bg-stone-200 rounded w-1/2 mb-3" />
              <div className="flex gap-2">
                <div className="h-3 bg-stone-200 rounded w-20" />
                <div className="h-3 bg-stone-200 rounded w-16" />
              </div>
            </div>
            <div className="hidden sm:block w-24 h-20 bg-stone-200 rounded-lg" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function MedioDetailPage() {
  const params = useParams();
  const id = params.id as string;
  
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'info' | 'articles'>('info');
  
  const medio = getMedioById(id);
  
  const today = format(new Date(), "EEEE, d 'de' MMMM 'de' yyyy", { locale: es });
  const todayCapitalized = today.charAt(0).toUpperCase() + today.slice(1);

  useEffect(() => {
    if (!id || !medio) return;
    
    async function fetchArticles() {
      try {
        setLoading(true);
        const res = await fetch(`${API_URL}/api/articles?source_id=${id}&limit=50`);
        if (!res.ok) throw new Error('Error fetching articles');
        
        const data = await res.json();
        setArticles(data.articles || []);
      } catch (err) {
        console.error('Error:', err);
        setError('No se pudieron cargar los artículos');
      } finally {
        setLoading(false);
      }
    }
    
    fetchArticles();
  }, [id, medio]);

  if (!medio) {
    return (
      <div className="min-h-screen bg-[#FDF6E3] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-serif font-bold text-stone-900 mb-2">Medio no encontrado</h1>
          <p className="text-stone-500 mb-4">No tenemos información sobre este medio.</p>
          <Link href="/medios" className="text-stone-600 hover:text-stone-900 underline">
            ← Volver a Medios
          </Link>
        </div>
      </div>
    );
  }

  const borderColor = medio.color === 'red' ? 'border-l-red-500' : 
                      medio.color === 'blue' ? 'border-l-blue-500' : 
                      'border-l-stone-400';
  const bgColor = medio.color === 'red' ? 'bg-red-50' : 
                  medio.color === 'blue' ? 'bg-blue-50' : 
                  'bg-stone-50';

  return (
    <div className="min-h-screen bg-[#FDF6E3]">
      {/* Header */}
      <header className="border-b-2 border-stone-800 bg-[#FDF6E3] sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center py-4 sm:py-5 border-b border-stone-300">
            <Link href="/">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-serif font-black tracking-tight text-stone-900">
                NEOTRAL
              </h1>
            </Link>
            <p className="text-xs sm:text-sm text-stone-600 italic mt-1 font-serif">
              Todas las versiones. Tú decides.
            </p>
          </div>
          
          <nav className="flex justify-start sm:justify-center gap-4 sm:gap-8 py-3 text-sm overflow-x-auto no-scrollbar">
            <Link href="/" className="font-serif text-stone-500 hover:text-stone-900 transition-colors whitespace-nowrap">
              Hoy
            </Link>
            <Link href="/radar" className="font-serif text-stone-500 hover:text-stone-900 transition-colors whitespace-nowrap">
              Radar
            </Link>
            <Link href="/medios" className="font-serif font-semibold text-stone-900 border-b border-stone-800 whitespace-nowrap">
              Medios
            </Link>
            <Link href="/silenciadas" className="font-serif text-stone-500 hover:text-stone-900 transition-colors whitespace-nowrap">
              Silenciadas
            </Link>
          </nav>
          
          <p className="text-xs text-stone-400 uppercase tracking-widest text-center py-2 font-mono">
            {todayCapitalized}
          </p>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        {/* Breadcrumb */}
        <nav className="mb-6 opacity-0 animate-fade-in" style={{ animationFillMode: 'forwards' }}>
          <Link 
            href="/medios" 
            className="text-sm text-stone-500 hover:text-stone-900 flex items-center gap-1 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Volver a Medios
          </Link>
        </nav>

        {/* Medio Header */}
        <div 
          className={`${bgColor} border-l-4 ${borderColor} rounded-r-lg p-6 mb-8 opacity-0 animate-fade-in`}
          style={{ animationDelay: '50ms', animationFillMode: 'forwards' }}
        >
          <div className="flex flex-col sm:flex-row items-start gap-5">
            {/* Logo */}
            <div className={`${medio.logoColor} w-24 h-24 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg`}>
              <span className="text-white font-bold text-2xl tracking-tight">
                {medio.initials}
              </span>
            </div>
            
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <h2 className="text-3xl sm:text-4xl font-serif font-bold text-stone-900">
                  {medio.name}
                </h2>
                <span className={`inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1 rounded-full ${
                  medio.color === 'red' ? 'bg-red-100 text-red-700' :
                  medio.color === 'blue' ? 'bg-blue-100 text-blue-700' :
                  'bg-stone-200 text-stone-600'
                }`}>
                  <span className={`w-2 h-2 rounded-full ${
                    medio.color === 'red' ? 'bg-red-500' :
                    medio.color === 'blue' ? 'bg-blue-500' :
                    'bg-stone-400'
                  }`} />
                  {medio.biasLabel}
                </span>
              </div>
              
              <p className="text-lg text-stone-600 mb-4" style={{ fontFamily: 'Georgia, serif' }}>
                {medio.tagline}
              </p>
              
              <div className="flex flex-wrap items-center gap-4">
                <a 
                  href={medio.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm bg-stone-800 text-white px-4 py-2 rounded-lg hover:bg-stone-700 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  Visitar {medio.name}
                </a>
                <div className="flex items-center gap-4 text-sm text-stone-500">
                  <span>📅 Fundado en {medio.founded.year}</span>
                  <span>·</span>
                  <span>🏢 {medio.ownership.group}</span>
                  {articles.length > 0 && (
                    <>
                      <span>·</span>
                      <span className="font-mono">{articles.length} artículos</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-stone-200">
          <button
            onClick={() => setActiveTab('info')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === 'info'
                ? 'border-stone-800 text-stone-900'
                : 'border-transparent text-stone-500 hover:text-stone-700'
            }`}
          >
            📋 Perfil completo
          </button>
          <button
            onClick={() => setActiveTab('articles')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === 'articles'
                ? 'border-stone-800 text-stone-900'
                : 'border-transparent text-stone-500 hover:text-stone-700'
            }`}
          >
            📰 Artículos ({articles.length})
          </button>
        </div>

        {activeTab === 'info' ? (
          /* Info Tab */
          <div className="grid gap-6 md:grid-cols-2">
            {/* Espectro */}
            <div className="md:col-span-2">
              <BiasIndicator medio={medio} />
            </div>
            
            {/* Propiedad */}
            <InfoSection title="Propiedad" icon="🏛️">
              <div className="space-y-3">
                <div>
                  <p className="font-semibold text-stone-800 text-lg">{medio.ownership.group}</p>
                  <p className="text-sm text-stone-500 capitalize">{medio.ownership.type}</p>
                </div>
                
                <div>
                  <p className="text-xs font-mono uppercase text-stone-400 mb-1">Propietarios principales</p>
                  <ul className="text-sm text-stone-600 space-y-1">
                    {medio.ownership.owners.map((owner, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-stone-300" />
                        {owner}
                      </li>
                    ))}
                  </ul>
                </div>
                
                {medio.ownership.notes && (
                  <p className="text-sm text-stone-500 italic border-l-2 border-stone-200 pl-3">
                    {medio.ownership.notes}
                  </p>
                )}
              </div>
            </InfoSection>
            
            {/* Historia */}
            <InfoSection title="Historia" icon="📜">
              <div className="space-y-3">
                <p className="text-2xl font-bold text-stone-800">{medio.founded.year}</p>
                <p className="text-sm text-stone-600 leading-relaxed">
                  {medio.founded.context}
                </p>
                
                {medio.founded.founders && medio.founded.founders.length > 0 && (
                  <div>
                    <p className="text-xs font-mono uppercase text-stone-400 mb-1">Fundadores</p>
                    <p className="text-sm text-stone-600">
                      {medio.founded.founders.join(', ')}
                    </p>
                  </div>
                )}
              </div>
            </InfoSection>
            
            {/* Línea editorial */}
            <InfoSection title="Línea editorial" icon="📰">
              <div className="space-y-4">
                <p className="text-sm text-stone-600 leading-relaxed">
                  {medio.editorial.summary}
                </p>
                
                <div>
                  <p className="text-xs font-mono uppercase text-stone-400 mb-2">Valores principales</p>
                  <div className="flex flex-wrap gap-2">
                    {medio.editorial.values.map(value => (
                      <span key={value} className="text-xs bg-stone-100 text-stone-700 px-2.5 py-1 rounded-full">
                        {value}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 pt-3 border-t border-stone-100">
                  <div>
                    <p className="text-xs font-mono uppercase text-stone-400 mb-1">Crítico con</p>
                    <ul className="text-xs text-stone-500 space-y-0.5">
                      {medio.editorial.criticalOf.slice(0, 3).map((item, i) => (
                        <li key={i}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="text-xs font-mono uppercase text-stone-400 mb-1">Afín a</p>
                    <ul className="text-xs text-stone-500 space-y-0.5">
                      {medio.editorial.alignedWith.slice(0, 3).map((item, i) => (
                        <li key={i}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
                
                <div className="pt-3 border-t border-stone-100">
                  <p className="text-xs font-mono uppercase text-stone-400 mb-1">Estilo periodístico</p>
                  <p className="text-sm text-stone-600">{medio.editorial.style}</p>
                </div>
              </div>
            </InfoSection>
            
            {/* Audiencia */}
            <InfoSection title="Audiencia" icon="👥">
              <div className="space-y-3">
                <p className="text-sm text-stone-600 leading-relaxed">
                  {medio.audience.profile}
                </p>
                
                <div>
                  <p className="text-xs font-mono uppercase text-stone-400 mb-2">Perfil demográfico</p>
                  <div className="flex flex-wrap gap-2">
                    {medio.audience.demographics.map(demo => (
                      <span key={demo} className="text-xs bg-stone-100 text-stone-600 px-2.5 py-1 rounded-full">
                        {demo}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div className="pt-3 border-t border-stone-100">
                  <p className="text-xs font-mono uppercase text-stone-400 mb-1">Alcance</p>
                  <p className="text-sm font-semibold text-stone-700">{medio.audience.reach}</p>
                </div>
              </div>
            </InfoSection>
            
            {/* Financiación y transparencia */}
            <InfoSection title="Financiación" icon="💰">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-stone-600">{medio.funding.model}</p>
                  <TransparencyBadge level={medio.funding.transparency} />
                </div>
                
                {medio.funding.notes && (
                  <p className="text-sm text-stone-500 italic border-l-2 border-stone-200 pl-3">
                    {medio.funding.notes}
                  </p>
                )}
                
                <div className="bg-stone-50 p-3 rounded-lg">
                  <p className="text-xs text-stone-500">
                    <strong>Nota:</strong> La transparencia financiera es clave para entender posibles 
                    conflictos de interés en la cobertura informativa.
                  </p>
                </div>
              </div>
            </InfoSection>
            
            {/* Controversias */}
            {medio.controversies && medio.controversies.length > 0 && (
              <InfoSection title="Controversias destacadas" icon="⚠️">
                <ul className="space-y-2">
                  {medio.controversies.map((controversy, i) => (
                    <li key={i} className="text-sm text-stone-600 flex items-start gap-2">
                      <span className="text-amber-500 mt-0.5">•</span>
                      {controversy}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-stone-400 mt-4 italic">
                  Esta lista no es exhaustiva y representa hechos documentados públicamente.
                </p>
              </InfoSection>
            )}
            
            {/* Meta info */}
            <div className="md:col-span-2 bg-stone-50 border border-stone-200 rounded-lg p-4 flex flex-wrap gap-6 text-sm">
              <div>
                <span className="text-stone-400">Formato:</span>
                <span className="ml-2 text-stone-700 capitalize">{medio.format}</span>
              </div>
              <div>
                <span className="text-stone-400">Frecuencia:</span>
                <span className="ml-2 text-stone-700 capitalize">{medio.frequency}</span>
              </div>
              <div>
                <span className="text-stone-400">Tipo:</span>
                <span className="ml-2 text-stone-700 capitalize">{medio.ownership.type}</span>
              </div>
            </div>
          </div>
        ) : (
          /* Articles Tab */
          <section>
            {loading ? (
              <LoadingSkeleton />
            ) : error ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
                <p className="text-amber-800">{error}</p>
              </div>
            ) : articles.length === 0 ? (
              <div className="bg-stone-50 border border-stone-200 rounded-lg p-8 text-center">
                <p className="text-stone-500">No hay artículos de este medio todavía.</p>
                <p className="text-sm text-stone-400 mt-2">
                  Los artículos se indexan automáticamente. Vuelve más tarde.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {articles.map((article, i) => (
                  <ArticleCard 
                    key={article.id} 
                    article={article} 
                    color={medio.color}
                    index={i}
                  />
                ))}
              </div>
            )}
          </section>
        )}

        {/* Related medios */}
        <div className="mt-12 pt-8 border-t border-stone-200">
          <h3 className="text-sm font-mono uppercase tracking-wider text-stone-400 mb-4">
            Otros medios del espectro
          </h3>
          <div className="flex flex-wrap gap-3">
            {getMediosSorted()
              .filter(m => m.id !== medio.id)
              .slice(0, 6)
              .map(m => (
                <Link
                  key={m.id}
                  href={`/medios/${m.id}`}
                  className="inline-flex items-center gap-2 bg-white border border-stone-200 rounded-lg px-3 py-2 hover:shadow-md hover:border-stone-300 transition-all"
                >
                  <div className={`${m.logoColor} w-6 h-6 rounded flex items-center justify-center`}>
                    <span className="text-white text-xs font-bold">{m.initials}</span>
                  </div>
                  <span className="text-sm text-stone-700">{m.name}</span>
                </Link>
              ))}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-stone-300 mt-12 bg-[#FDF6E3]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 text-center">
          <p className="text-xs text-stone-400 font-serif">
            © {new Date().getFullYear()} Neotral · Fundación MAFT
          </p>
        </div>
      </footer>
    </div>
  );
}
